import { useEffect, useId, useMemo, useState, type FormEvent, type MouseEvent } from 'react'
import { parseNoteText, UAT_NOTE_FIELD_LABEL, UAT_NOTE_FIELDS, UAT_NOTE_LIMITS, uatNoteHash, type UatNoteField } from '../lib/uat-notes'
import { UAT_STATUS_SHORT, UAT_STATUS_TONE } from '../lib/uat'
import type { ThreadReply, UatItem, UatNote, UatNoteImage, UatProject } from '../types'
import { Button, Dialog, Field, StatusBadge } from './ui'
import { ArrowLeft, ChatAdd, ChevronDown, File as FileIcon, SpinnerGap } from './ui/icons'
import { termsInline } from './OrderFormDocument'
import { formatThreadTime, ThreadReplies } from './ThreadReplies'

/** "Nov 3, 2026": the "Last updated" date of a section. */
export function noteDate(value: string): string {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** The newest "Last updated" date of a list of sections. */
export function latestNoteDate(notes: ReadonlyArray<Pick<UatNote, 'updatedAt'>>): string | null {
  const latest = notes.map((note) => note.updatedAt).filter(Boolean).sort().at(-1)
  return latest ? noteDate(latest) : null
}

/**
 * One field of a section in the safe text format (src/lib/uat-notes.ts):
 * paragraphs, headings, bulleted and numbered lists with one level of
 * sub-items, tables, bold, italic and links. Built as elements, never as HTML.
 */
export function NoteText({ source, label = 'Table' }: { source: string; label?: string }) {
  const blocks = useMemo(() => parseNoteText(source), [source])
  return (
    <div className="uat-note-text">
      {blocks.map((block, index) => {
        if (block.type === 'heading') return <p key={index} className="uat-note-subhead">{termsInline(block.children)}</p>
        if (block.type === 'paragraph') return <p key={index}>{termsInline(block.children)}</p>
        if (block.type === 'table') {
          return (
            <div key={index} className="uat-note-table-wrap" role="region" aria-label={label} tabIndex={0}>
              <table className="uat-note-table">
                {block.header && <thead><tr>{block.header.map((cell, cellIndex) => <th key={cellIndex} scope="col">{termsInline(cell)}</th>)}</tr></thead>}
                <tbody>
                  {block.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>{row.map((cell, cellIndex) => cellIndex === 0 && block.header ? <th key={cellIndex} scope="row">{termsInline(cell)}</th> : <td key={cellIndex}>{termsInline(cell)}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
        const items = block.items.map((item, position) => (
          <li key={position}>
            {termsInline(item.children)}
            {item.items.length > 0 && <ul>{item.items.map((sub, subIndex) => <li key={subIndex}>{termsInline(sub)}</li>)}</ul>}
          </li>
        ))
        return block.ordered ? <ol key={index} start={block.start === 1 ? undefined : block.start}>{items}</ol> : <ul key={index}>{items}</ul>
      })}
    </div>
  )
}

/** The field label a customer reads. "Notes for This School" names the customer. */
export function noteFieldLabel(field: UatNoteField, customerLabel: string): string {
  return field === 'schoolNotes' ? `Notes for ${customerLabel || 'Your School'}` : UAT_NOTE_FIELD_LABEL[field]
}

/** The template fields of one section, then its screenshots. Shared by the customer page and the admin preview. */
export function NoteFields({ note, customerLabel, onEnlarge }: { note: Pick<UatNote, 'title' | UatNoteField> & { images: UatNoteImage[] }; customerLabel: string; onEnlarge: (image: UatNoteImage) => void }) {
  return (
    <>
      {UAT_NOTE_FIELDS.filter((field) => note[field].trim()).map((field) => (
        <div key={field} className={`uat-note-field uat-note-field--${field}`}>
          <h5>{noteFieldLabel(field, customerLabel)}</h5>
          <NoteText source={note[field]} label={`${noteFieldLabel(field, customerLabel)} table, ${note.title}`} />
        </div>
      ))}
      {note.images.length > 0 && (
        <div className="uat-note-field">
          <h5>Screenshots</h5>
          <ul className="uat-note-shots">
            {note.images.map((image, index) => (
              <li key={image.id}>
                <figure>
                  <button type="button" className="uat-note-shot" onClick={() => onEnlarge(image)}>
                    <img src={image.url} alt={image.caption || `Screenshot ${index + 1} of ${note.title}`} loading="lazy" decoding="async" />
                    <span className="ui-visually-hidden">Enlarge screenshot {index + 1}</span>
                  </button>
                  {image.caption && <figcaption>{image.caption}</figcaption>}
                </figure>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  )
}

/** A screenshot at full size, with its caption. */
export function ScreenshotDialog({ image, onClose }: { image: UatNoteImage | null; onClose: () => void }) {
  if (!image) return null
  return (
    <Dialog open onClose={onClose} title="Screenshot" description={image.caption || undefined} className="uat-lightbox">
      <img className="uat-lightbox-image" src={image.url} alt={image.caption || 'Screenshot'} />
    </Dialog>
  )
}

type ThreadProps = {
  canReply: boolean
  canModerate: boolean
  pending: Set<string>
  onReply: (parentId: string, body: string) => Promise<boolean>
  onModerate: (replyId: string, change: { body?: string; hidden?: boolean }) => Promise<boolean>
}

/**
 * A project's What's Changing page (Rod, 2026-11-04): a readable page with a
 * sticky list of sections, each with its template fields, screenshots (select
 * to enlarge), "Last updated", the UAT items it explains and its comments.
 */
export function ReleaseNotesView({ project, notes, items, customerLabel, canEdit, thread, focusNoteId, onBack, onOpenItem, onFocusNote, onComment }: {
  project: UatProject
  notes: UatNote[]
  items: UatItem[]
  customerLabel: string
  canEdit: boolean
  thread: ThreadProps
  focusNoteId: string | null
  onBack: () => void
  onOpenItem: (itemId: string) => void
  onFocusNote: (noteId: string) => void
  onComment: (note: UatNote, body: string) => Promise<boolean>
}) {
  const headingId = useId()
  const tocId = useId()
  const [active, setActive] = useState<string | null>(focusNoteId || notes[0]?.id || null)
  const [tocOpen, setTocOpen] = useState(false)
  const [enlarged, setEnlarged] = useState<UatNoteImage | null>(null)
  const itemsById = useMemo(() => new Map(items.map((item) => [item.id, item])), [items])
  const updated = latestNoteDate(notes)

  // Opening a section from an item or a link scrolls to it and moves focus there.
  useEffect(() => {
    if (!focusNoteId) return
    const target = document.getElementById(`uat-note-${focusNoteId}`)
    if (!target) return
    target.scrollIntoView({ block: 'start' })
    target.focus({ preventScroll: true })
    setActive(focusNoteId)
  }, [focusNoteId])

  // The section list marks the section being read.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
      if (visible) setActive(visible.target.getAttribute('data-note-id'))
    }, { rootMargin: '0px 0px -65% 0px' })
    for (const note of notes) {
      const element = document.getElementById(`uat-note-${note.id}`)
      if (element) observer.observe(element)
    }
    return () => observer.disconnect()
  }, [notes])

  function jump(event: MouseEvent<HTMLAnchorElement>, noteId: string) {
    event.preventDefault()
    setTocOpen(false)
    // Scroll here too: a second select of the same section does not change focusNoteId.
    const target = document.getElementById(`uat-note-${noteId}`)
    target?.scrollIntoView({ block: 'start' })
    target?.focus({ preventScroll: true })
    setActive(noteId)
    onFocusNote(noteId)
  }

  return (
    <section className="uat-notes" aria-labelledby={headingId}>
      <div className="uat-notes-head">
        <Button type="button" variant="secondary" compact onClick={onBack}><ArrowLeft size={16} aria-hidden="true" />Back to UAT Items</Button>
        <div className="uat-notes-title">
          <span className="section-label">What's Changing</span>
          <h3 id={headingId}>{project.name}</h3>
          {project.description && <p className="uat-notes-lede">{project.description}</p>}
          <p className="uat-notes-meta">{notes.length} {notes.length === 1 ? 'section' : 'sections'}{updated ? ` · Last updated ${updated}` : ''}</p>
        </div>
      </div>
      <div className="uat-notes-layout">
        <nav className="uat-notes-toc" aria-label={`Sections of ${project.name}`}>
          <button type="button" className="uat-notes-toc-toggle" aria-expanded={tocOpen} aria-controls={tocId} onClick={() => setTocOpen((value) => !value)}>
            <ChevronDown size={16} aria-hidden="true" className={tocOpen ? 'is-open' : ''} />
            Sections ({notes.length})
          </button>
          <p className="uat-notes-toc-title" aria-hidden="true">Sections</p>
          <ol id={tocId} className={`uat-notes-toc-list${tocOpen ? ' is-open' : ''}`}>
            {notes.map((note) => (
              <li key={note.id}>
                <a href={uatNoteHash(note.id)} aria-current={active === note.id ? 'location' : undefined} onClick={(event) => jump(event, note.id)}>
                  <span>{note.title}</span>
                  <small>Updated {noteDate(note.updatedAt)}</small>
                </a>
              </li>
            ))}
          </ol>
        </nav>
        <div className="uat-notes-body">
          {notes.map((note) => (
            <NoteSection
              key={note.id}
              note={note}
              items={note.itemIds.map((id) => itemsById.get(id)).filter((item): item is UatItem => Boolean(item))}
              customerLabel={customerLabel}
              canEdit={canEdit}
              thread={thread}
              onEnlarge={setEnlarged}
              onOpenItem={onOpenItem}
              onComment={onComment}
            />
          ))}
        </div>
      </div>
      <ScreenshotDialog image={enlarged} onClose={() => setEnlarged(null)} />
    </section>
  )
}

function NoteSection({ note, items, customerLabel, canEdit, thread, onEnlarge, onOpenItem, onComment }: {
  note: UatNote
  items: UatItem[]
  customerLabel: string
  canEdit: boolean
  thread: ThreadProps
  onEnlarge: (image: UatNoteImage) => void
  onOpenItem: (itemId: string) => void
  onComment: (note: UatNote, body: string) => Promise<boolean>
}) {
  const titleId = useId()
  return (
    <article id={`uat-note-${note.id}`} data-note-id={note.id} className="uat-note" aria-labelledby={titleId} tabIndex={-1}>
      <header className="uat-note-head">
        <h4 id={titleId}>{note.title}</h4>
        <p className="uat-note-updated">Last updated <time dateTime={note.updatedAt}>{noteDate(note.updatedAt)}</time></p>
      </header>
      <NoteFields note={note} customerLabel={customerLabel} onEnlarge={onEnlarge} />
      <NoteItems items={items} onOpenItem={onOpenItem} />
      <NoteComments note={note} canEdit={canEdit} thread={thread} onComment={onComment} />
    </article>
  )
}

function NoteComments({ note, canEdit, thread, onComment }: { note: UatNote; canEdit: boolean; thread: ThreadProps; onComment: (note: UatNote, body: string) => Promise<boolean> }) {
  const [composing, setComposing] = useState(false)
  const [text, setText] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const trimmed = text.trim()

  async function send(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    if (!trimmed) { setError('Write a comment before you send it.'); return }
    if (trimmed.length > UAT_NOTE_LIMITS.commentChars) { setError(`Keep the comment under ${UAT_NOTE_LIMITS.commentChars.toLocaleString('en-US')} characters.`); return }
    setError('')
    setBusy(true)
    const sent = await onComment(note, trimmed)
    setBusy(false)
    if (sent) { setText(''); setComposing(false) }
  }

  return (
    <div className="uat-block uat-comments uat-note-comments">
      <div className="uat-block-head">
        <h5>Comments <span>{note.comments.length}</span></h5>
        {canEdit && !composing && <Button type="button" variant="secondary" compact onClick={() => setComposing(true)}><ChatAdd size={14} aria-hidden="true" />Comment</Button>}
      </div>
      {canEdit && composing && (
        <form className="thread-form" onSubmit={(event) => void send(event)} noValidate>
          <Field label="Your Comment" error={error || undefined} hint={`${trimmed.length.toLocaleString('en-US')} of ${UAT_NOTE_LIMITS.commentChars.toLocaleString('en-US')} characters. The Prentus team gets it right away.`}>
            <textarea value={text} rows={3} maxLength={UAT_NOTE_LIMITS.commentChars} autoFocus onChange={(event) => setText(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) void send(event) }} />
          </Field>
          <div className="thread-form-actions">
            <Button type="submit" compact disabled={busy}>{busy && <SpinnerGap size={14} className="spin" aria-hidden="true" />}Send Comment</Button>
            <Button type="button" variant="secondary" compact disabled={busy} onClick={() => { setComposing(false); setError('') }}>Cancel</Button>
          </div>
        </form>
      )}
      {note.comments.length === 0
        ? <p className="uat-comments-empty">{canEdit ? 'No comments yet. Select Comment to ask how this should work or to tell the Prentus team what you see. Your team and Prentus reply here.' : 'No comments yet. Customer comments on this section show here, and both sides reply in the thread.'}</p>
        : (
          <ol className="uat-comment-list">
            {note.comments.map((entry) => (
              <li key={entry.id} className="uat-comment">
                <p className="plan-note-head"><strong>{entry.authorName}</strong><time dateTime={entry.createdAt}>{formatThreadTime(entry.createdAt)}</time></p>
                {entry.body && <p className="plan-note-body">{entry.body}</p>}
                <ThreadReplies replies={entry.replies} label={`${entry.authorName}'s comment on ${note.title}`} canReply={thread.canReply} canModerate={thread.canModerate} assistantPending={thread.pending.has(entry.id)} onReply={(body) => thread.onReply(entry.id, body)} onModerate={thread.onModerate} />
              </li>
            ))}
          </ol>
        )}
    </div>
  )
}

/** The UAT items a section explains. Without onOpenItem (the admin preview) they are not buttons. */
export function NoteItems({ items, onOpenItem }: { items: Array<Pick<UatItem, 'id' | 'title' | 'status' | 'linearIdentifier'>>; onOpenItem?: (itemId: string) => void }) {
  if (!items.length) return null
  return (
    <div className="uat-note-field">
      <h5>UAT Items for This Section</h5>
      <ul className="uat-note-items">
        {items.map((item) => {
          const content = (
            <>
              <StatusBadge tone={UAT_STATUS_TONE[item.status]}>{UAT_STATUS_SHORT[item.status]}</StatusBadge>
              <span className="uat-note-item-title">{item.title}</span>
              {item.linearIdentifier && <span className="uat-row-ticket">{item.linearIdentifier}</span>}
            </>
          )
          return (
            <li key={item.id}>
              {onOpenItem
                ? <button type="button" className="uat-note-item" onClick={() => onOpenItem(item.id)}>{content}<span className="ui-visually-hidden">, open the item</span></button>
                : <span className="uat-note-item">{content}</span>}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/** "How It Should Work" in an item: links to the sections that explain it. */
export function HowItShouldWork({ notes, onOpen }: { notes: UatNote[]; onOpen: (note: UatNote) => void }) {
  if (!notes.length) return null
  return (
    <div className="uat-how">
      <h4><FileIcon size={16} aria-hidden="true" />How It Should Work</h4>
      <ul>
        {notes.map((note) => (
          <li key={note.id}>
            <a href={uatNoteHash(note.id)} onClick={(event) => { event.preventDefault(); onOpen(note) }}>
              What's Changing: {note.title}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** A comment added in the local preview, without a request. */
export function previewNoteComment(note: UatNote, body: string, author: string): UatNote {
  const now = new Date().toISOString()
  const replies: ThreadReply[] = []
  return { ...note, comments: [...note.comments, { id: `preview-${Date.now()}`, kind: 'section_comment', authorName: author, authorSide: 'customer', body, fromValue: null, toValue: null, caseText: null, createdAt: now, replies, threaded: true }] }
}
